import { createContext } from 'react'

import type { ReactNode } from 'react'

import { themeConfig } from '@/config'
import useLocalStorage from '@/hooks/useLocalStorage'
import type {
	CustomizationProps,
	FontFamily,
	I18n,
	ThemeDirection,
	ThemeMode,
} from '@/themes/config'

// initial state
const initialState: CustomizationProps = {
	...themeConfig,
	onChangeContainer: () => {},
	onChangeLocalization: () => {},
	onChangeMode: () => {},
	onChangeDirection: () => {},
	onChangeMiniDrawer: () => {},
	onChangeFontFamily: () => {},
}

// ==============================|| CONFIG CONTEXT & PROVIDER ||============================== //

const ConfigContext = createContext(initialState)

type ConfigProviderProps = {
	children: ReactNode
}

function ConfigProvider({ children }: ConfigProviderProps) {
	const [config, setConfig] = useLocalStorage(
		'mantis-react-ts-config',
		initialState
	)

	const onChangeContainer = () => {
		setConfig({
			...config,
			container: !config.container,
		})
	}

	const onChangeLocalization = (lang: I18n) => {
		setConfig({
			...config,
			i18n: lang,
		})
	}

	const onChangeMode = (mode: ThemeMode) => {
		setConfig({
			...config,
			mode,
		})
	}

	const onChangeDirection = (direction: ThemeDirection) => {
		setConfig({
			...config,
			themeDirection: direction,
		})
	}

	const onChangeMiniDrawer = (miniDrawer: boolean) => {
		setConfig({
			...config,
			miniDrawer,
		})
	}

	const onChangeFontFamily = (fontFamily: FontFamily) => {
		setConfig({
			...config,
			fontFamily,
		})
	}

	return (
		<ConfigContext.Provider
			value={{
				...config,
				onChangeContainer,
				onChangeLocalization,
				onChangeMode,
				onChangeDirection,
				onChangeMiniDrawer,
				onChangeFontFamily,
			}}
		>
			{children}
		</ConfigContext.Provider>
	)
}

if (process.env.NODE_ENV !== 'production') {
	ConfigContext.displayName = 'ConfigContext'
}

export { ConfigContext, ConfigProvider }
